import { z } from 'zod';

import {
  ActivityIdSchema,
  AgentIdSchema,
  AgentSkillIdSchema,
  AssetIdSchema,
  CeremonyIdSchema,
  CommunicationArtifactIdSchema,
  DeliverableIdSchema,
  ElementIdSchema,
  GateIdSchema,
  LensIdSchema,
  McpServerIdSchema,
  MethodIdSchema,
  PhaseIdSchema,
  RoleIdSchema,
  StageIdSchema,
  TailoringDecisionIdSchema,
  TemplateVersionIdSchema,
  ToolIdSchema,
  WorkPracticeIdSchema,
} from '../ids/index.js';
import { NODE_TYPES, type NodeTypeIri } from '../vocabulary/index.js';
import {
  AutomationLevelSchema,
  McpTransportSchema,
  MethodStatusSchema,
  TailoringDecisionValueSchema,
  type RoleKind,
} from './enums.js';

const label = z.string().min(1, 'name must not be empty');
const text = z.string();
const order = z.number().int().nonnegative();
const strings = z.array(z.string());

/**
 * Fields every node may carry. `writeup` is the long-form markdown the studio
 * authors; `description` stays the one-line summary the explorer renders.
 */
const DESCRIBED = {
  name: label,
  description: text.optional(),
  writeup: text.optional(),
  order: order.optional(),
} as const;

/**
 * Where a catalogue node came from, carried by the node itself.
 *
 * Only client entries say anything: absence means the platform catalogue, so every
 * method published before adoption catalogues existed keeps its meaning unchanged.
 * `standsInFor` is a plain id and not a reference term — the entry it names is the
 * one the client replaced, so it is deliberately *not* in the graph, and making it
 * a reference would fire MTH-001 on every substitution.
 */
const PROVENANCE = {
  provenance: z.literal('client').optional(),
  broughtBy: text.optional(),
  standsInFor: ElementIdSchema.optional(),
} as const;

// --- Method ----------------------------------------------------------------

export const MethodSchema = z.strictObject({
  '@id': MethodIdSchema,
  '@type': z.literal(NODE_TYPES.Method),
  ...DESCRIBED,
  status: MethodStatusSchema.optional(),
  revision: z.number().int().nonnegative().optional(),
  /** A variant is an edge, not a type — agentic is a variant like any other. */
  variantOf: MethodIdSchema.optional(),
  cycleTimeDelta: text.optional(),
  hasPhase: z.array(PhaseIdSchema).optional(),
  hasStage: z.array(StageIdSchema).optional(),
  hasWorkPractice: z.array(WorkPracticeIdSchema).optional(),
  hasRole: z.array(RoleIdSchema).optional(),
  hasGate: z.array(GateIdSchema).optional(),
  hasDeliverable: z.array(DeliverableIdSchema).optional(),
  hasLens: z.array(LensIdSchema).optional(),
  hasCeremony: z.array(CeremonyIdSchema).optional(),
  hasTool: z.array(ToolIdSchema).optional(),
  hasAsset: z.array(AssetIdSchema).optional(),
  hasAgent: z.array(AgentIdSchema).optional(),
  hasAgentSkill: z.array(AgentSkillIdSchema).optional(),
  hasMcpServer: z.array(McpServerIdSchema).optional(),
  /**
   * The nodes in this method the client brought. Derived on export from the nodes
   * that carry `provenance: "client"`, so it is a reference list that closes by
   * construction; MTH-034 is the check for a document written by hand.
   */
  clientContributions: z.array(ElementIdSchema).optional(),
});

// --- Phase -----------------------------------------------------------------

export const PhaseSchema = z.strictObject({
  '@id': PhaseIdSchema,
  '@type': z.literal(NODE_TYPES.Phase),
  ...DESCRIBED,
  hasStage: z.array(StageIdSchema).optional(),
  hasWorkPractice: z.array(WorkPracticeIdSchema).optional(),
  precedes: z.array(PhaseIdSchema).optional(),
  workStream: strings.optional(),
});

// --- Stage -----------------------------------------------------------------

export const StageSchema = z.strictObject({
  '@id': StageIdSchema,
  '@type': z.literal(NODE_TYPES.Stage),
  ...DESCRIBED,
  hasActivity: z.array(ActivityIdSchema).optional(),
  hasWorkPractice: z.array(WorkPracticeIdSchema).optional(),
  inPhase: PhaseIdSchema.optional(),
  /** Legacy string form of the phase; preserved on export (§3). */
  phase: text.optional(),
  workStream: strings.optional(),
  isMetaStage: z.boolean().optional(),
  governedByGate: GateIdSchema.optional(),
  /**
   * A stage's own exit criteria, distinct from the gate that formalises them: a
   * stage may state what "done" means without a gate ceremony existing yet.
   */
  exitCriteria: text.optional(),
  automation: AutomationLevelSchema.optional(),
  agenticShift: text.optional(),
  effortShift: text.optional(),
  cycleTimeDelta: text.optional(),
  precedes: z.array(StageIdSchema).optional(),
  dependsOn: z.array(StageIdSchema).optional(),
  loopsTo: z.array(ElementIdSchema).optional(),
});

// --- WorkPractice ----------------------------------------------------------

export const WorkPracticeSchema = z.strictObject({
  '@id': WorkPracticeIdSchema,
  '@type': z.literal(NODE_TYPES.WorkPractice),
  ...DESCRIBED,
  /** Practices nest, so a method can be a taxonomy rather than a spine. */
  parentPractice: WorkPracticeIdSchema.optional(),
  hasWorkPractice: z.array(WorkPracticeIdSchema).optional(),
  hasActivity: z.array(ActivityIdSchema).optional(),
  hasDeliverable: z.array(DeliverableIdSchema).optional(),
  inPhase: PhaseIdSchema.optional(),
  workStream: strings.optional(),
  workPractice: text.optional(),
  mandatory: z.boolean().optional(),
  governedByGate: GateIdSchema.optional(),
  precedes: z.array(WorkPracticeIdSchema).optional(),
  dependsOn: z.array(WorkPracticeIdSchema).optional(),
});

// --- Activity --------------------------------------------------------------

export const ActivitySchema = z.strictObject({
  '@id': ActivityIdSchema,
  '@type': z.literal(NODE_TYPES.Activity),
  ...DESCRIBED,
  purpose: text.optional(),
  performedBy: z.array(RoleIdSchema).optional(),
  produces: z.array(DeliverableIdSchema).optional(),
  consumes: z.array(DeliverableIdSchema).optional(),
  usesLens: z.array(LensIdSchema).optional(),
  usesTool: z.array(ToolIdSchema).optional(),
  usesAsset: z.array(AssetIdSchema).optional(),
  /** All five catalogues attach directly to an activity, not only via a role. */
  usesAgent: z.array(AgentIdSchema).optional(),
  usesAgentSkill: z.array(AgentSkillIdSchema).optional(),
  usesMcpServer: z.array(McpServerIdSchema).optional(),
  governedByGate: GateIdSchema.optional(),
  communication: z.array(CeremonyIdSchema).optional(),
  precedes: z.array(ActivityIdSchema).optional(),
  dependsOn: z.array(ActivityIdSchema).optional(),
  loopsTo: z.array(ActivityIdSchema).optional(),
  inPhase: PhaseIdSchema.optional(),
  inWorkPractice: WorkPracticeIdSchema.optional(),
  phase: text.optional(),
  workPractice: text.optional(),
  workStream: strings.optional(),
  agentRole: RoleIdSchema.optional(),
  humanRole: RoleIdSchema.optional(),
  automation: AutomationLevelSchema.optional(),
  inputs: strings.optional(),
  outputs: strings.optional(),
  mandatory: z.boolean().optional(),
  /**
   * The two agentic-narrative fields, which a Stage has had since the beginning and an
   * Activity was missing.
   *
   * THE BUG THIS EXISTS FOR. An activity is where automation is actually described — the
   * contract offers both fields on `activity`, the column stores them and the serialiser
   * emits them — but this schema is `strictObject`, so the *first* graph containing one
   * failed to serialise: every save of an activity's agentic shift answered
   * `@graph.N Unrecognized keys: "agenticShift", "effortShift"` with a 422, and the field
   * the whole compare surface quotes could not be authored at all. The terms were already
   * declared in `vocabulary/terms.ts` and already accepted on `Stage`; only this node's
   * key list disagreed.
   */
  agenticShift: text.optional(),
  effortShift: text.optional(),
});

// --- Role ------------------------------------------------------------------

export const RoleSchema = z.strictObject({
  '@id': RoleIdSchema,
  '@type': z.literal(NODE_TYPES.Role),
  ...DESCRIBED,
  responsibilities: strings.optional(),
  /** Discriminates HUMAN from AGENT; read it through `roleKind()`. */
  isAgentRole: z.boolean().optional(),
  agenticShift: text.optional(),
  implementsRole: RoleIdSchema.optional(),
  /** The catalogued agent that realises an agentic role. */
  realisedByAgent: AgentIdSchema.optional(),
});

/** One Role type, two kinds. Never branch on `@type` for this. */
export function roleKind(role: Role): RoleKind {
  return role.isAgentRole === true ? 'AGENT' : 'HUMAN';
}

// --- Gate (Gate | GateAgentic are the same shape) ---------------------------

const GATE_FIELDS = {
  '@id': GateIdSchema,
  ...DESCRIBED,
  exitCriteria: text.optional(),
  performedBy: z.array(RoleIdSchema).optional(),
  reviewEquivalent: text.optional(),
  automatedCheck: text.optional(),
  humanApproval: text.optional(),
  mandatory: z.boolean().optional(),
  /** Backward edges such as G5 → S4. Legal by construction, never a cycle. */
  loopsTo: z.array(ElementIdSchema).optional(),
} as const;

const GateBaseSchema = z.strictObject({
  ...GATE_FIELDS,
  '@type': z.literal(NODE_TYPES.Gate),
});
const GateAgenticSchema = z.strictObject({
  ...GATE_FIELDS,
  '@type': z.literal(NODE_TYPES.GateAgentic),
});
export const GateSchema = z.union([GateBaseSchema, GateAgenticSchema]);

// --- Deliverable (Template | Deliverable) ----------------------------------

const DELIVERABLE_FIELDS = {
  '@id': DeliverableIdSchema,
  ...DESCRIBED,
  templateFile: text.optional(),
  mandatory: z.boolean().optional(),
  workStream: strings.optional(),
  workPractice: text.optional(),
  category: text.optional(),
  hasTemplateVersion: z.array(TemplateVersionIdSchema).optional(),
  currentTemplateVersion: TemplateVersionIdSchema.optional(),
  links: strings.optional(),
  attachments: strings.optional(),
  ...PROVENANCE,
} as const;

const TemplateAliasSchema = z.strictObject({
  ...DELIVERABLE_FIELDS,
  '@type': z.literal(NODE_TYPES.Template),
});
const DeliverableCanonicalSchema = z.strictObject({
  ...DELIVERABLE_FIELDS,
  '@type': z.literal(NODE_TYPES.Deliverable),
});
export const DeliverableSchema = z.union([TemplateAliasSchema, DeliverableCanonicalSchema]);

// --- TemplateVersion -------------------------------------------------------

export const TemplateVersionSchema = z.strictObject({
  '@id': TemplateVersionIdSchema,
  '@type': z.literal(NODE_TYPES.TemplateVersion),
  name: label.optional(),
  description: text.optional(),
  order: order.optional(),
  version: z.number().int().positive(),
  /** The markdown template body itself. This is the payload of a version. */
  body: text,
  templateOf: DeliverableIdSchema.optional(),
});

// --- Lens (ArchitectureLens | Lens) ---------------------------------------

const LENS_FIELDS = {
  '@id': LensIdSchema,
  ...DESCRIBED,
  workStreamMapping: text.optional(),
  /** The review prompts an author works through when applying the lens. */
  checklist: strings.optional(),
} as const;

const ArchitectureLensSchema = z.strictObject({
  ...LENS_FIELDS,
  '@type': z.literal(NODE_TYPES.ArchitectureLens),
});
const LensCanonicalSchema = z.strictObject({
  ...LENS_FIELDS,
  '@type': z.literal(NODE_TYPES.Lens),
});
export const LensSchema = z.union([ArchitectureLensSchema, LensCanonicalSchema]);

// --- Ceremony (Communication | Ceremony) ----------------------------------

const CEREMONY_FIELDS = {
  '@id': CeremonyIdSchema,
  ...DESCRIBED,
  when: text.optional(),
  performedBy: z.array(RoleIdSchema).optional(),
} as const;

const CommunicationSchema = z.strictObject({
  ...CEREMONY_FIELDS,
  '@type': z.literal(NODE_TYPES.Communication),
});
const CeremonyCanonicalSchema = z.strictObject({
  ...CEREMONY_FIELDS,
  '@type': z.literal(NODE_TYPES.Ceremony),
});
export const CeremonySchema = z.union([CommunicationSchema, CeremonyCanonicalSchema]);

// --- CommunicationArtifact -------------------------------------------------

export const CommunicationArtifactSchema = z.strictObject({
  '@id': CommunicationArtifactIdSchema,
  '@type': z.literal(NODE_TYPES.CommunicationArtifact),
  ...DESCRIBED,
  templateFile: text.optional(),
  audience: text.optional(),
  format: text.optional(),
  when: text.optional(),
});

// --- Catalogue: Tool, Asset, Agent, AgentSkill, McpServer -----------------

const CATALOGUE_FIELDS = {
  ...DESCRIBED,
  category: text.optional(),
  capability: strings.optional(),
  links: strings.optional(),
  attachments: strings.optional(),
  screenshots: strings.optional(),
  ...PROVENANCE,
} as const;

export const ToolSchema = z.strictObject({
  '@id': ToolIdSchema,
  '@type': z.literal(NODE_TYPES.Tool),
  ...CATALOGUE_FIELDS,
});

export const AssetSchema = z.strictObject({
  '@id': AssetIdSchema,
  '@type': z.literal(NODE_TYPES.Asset),
  ...CATALOGUE_FIELDS,
});

export const AgentSchema = z.strictObject({
  '@id': AgentIdSchema,
  '@type': z.literal(NODE_TYPES.Agent),
  ...CATALOGUE_FIELDS,
  model: text.optional(),
  /** The method-graph Role this catalogued agent fulfils, if any. */
  implementsRole: RoleIdSchema.optional(),
  hasSkill: z.array(AgentSkillIdSchema).optional(),
  usesMcpServer: z.array(McpServerIdSchema).optional(),
  usesTool: z.array(ToolIdSchema).optional(),
});

export const AgentSkillSchema = z.strictObject({
  '@id': AgentSkillIdSchema,
  '@type': z.literal(NODE_TYPES.AgentSkill),
  ...CATALOGUE_FIELDS,
  usesMcpServer: z.array(McpServerIdSchema).optional(),
  usesTool: z.array(ToolIdSchema).optional(),
});

export const McpServerSchema = z.strictObject({
  '@id': McpServerIdSchema,
  '@type': z.literal(NODE_TYPES.McpServer),
  ...CATALOGUE_FIELDS,
  endpoint: text.optional(),
  transport: McpTransportSchema.optional(),
});

// --- TailoringDecision -----------------------------------------------------

export const TailoringDecisionSchema = z.strictObject({
  '@id': TailoringDecisionIdSchema,
  '@type': z.literal(NODE_TYPES.TailoringDecision),
  name: label.optional(),
  description: text.optional(),
  writeup: text.optional(),
  order: order.optional(),
  decision: TailoringDecisionValueSchema,
  appliesTo: ElementIdSchema,
  rationale: text.optional(),
  decidedBy: RoleIdSchema.optional(),
  /** An ADAPT decision may pin a rewritten template body. */
  templateOverride: TemplateVersionIdSchema.optional(),
});

// --- Union -----------------------------------------------------------------

/**
 * Discriminated on `@type`. Alias pairs contribute one branch each, so
 * `dpem:Gate` and `dpem:GateAgentic` both resolve without a lookahead.
 */
export const MethodNodeSchema = z.discriminatedUnion('@type', [
  MethodSchema,
  PhaseSchema,
  StageSchema,
  WorkPracticeSchema,
  ActivitySchema,
  RoleSchema,
  GateBaseSchema,
  GateAgenticSchema,
  TemplateAliasSchema,
  DeliverableCanonicalSchema,
  TemplateVersionSchema,
  ArchitectureLensSchema,
  LensCanonicalSchema,
  CommunicationSchema,
  CeremonyCanonicalSchema,
  CommunicationArtifactSchema,
  ToolSchema,
  AssetSchema,
  AgentSchema,
  AgentSkillSchema,
  McpServerSchema,
  TailoringDecisionSchema,
]);

export type Method = z.infer<typeof MethodSchema>;
export type Phase = z.infer<typeof PhaseSchema>;
export type Stage = z.infer<typeof StageSchema>;
export type WorkPractice = z.infer<typeof WorkPracticeSchema>;
export type Activity = z.infer<typeof ActivitySchema>;
export type Role = z.infer<typeof RoleSchema>;
export type Gate = z.infer<typeof GateSchema>;
export type Deliverable = z.infer<typeof DeliverableSchema>;
export type TemplateVersion = z.infer<typeof TemplateVersionSchema>;
export type Lens = z.infer<typeof LensSchema>;
export type Ceremony = z.infer<typeof CeremonySchema>;
export type CommunicationArtifact = z.infer<typeof CommunicationArtifactSchema>;
export type Tool = z.infer<typeof ToolSchema>;
export type Asset = z.infer<typeof AssetSchema>;
export type Agent = z.infer<typeof AgentSchema>;
export type AgentSkill = z.infer<typeof AgentSkillSchema>;
export type McpServer = z.infer<typeof McpServerSchema>;
export type TailoringDecision = z.infer<typeof TailoringDecisionSchema>;
export type MethodNode = z.infer<typeof MethodNodeSchema>;

/** Narrow a node to one of the `@type` values in `types`. */
export function isNodeOfType<T extends NodeTypeIri>(
  node: MethodNode,
  ...types: readonly T[]
): node is Extract<MethodNode, { '@type': T }> {
  return (types as readonly string[]).includes(node['@type']);
}
